import useCustomMutation from "@/composables/useCustomMutation";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { User } from "@/modules/users/interfaces/user";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";

interface Params {
  groupId: string | number;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

const getGroupUsers = async (params: Params) => {
  const { data } = await axios.get<User[]>(
    `/api/groups/${params.groupId}/users`,
  );
  return data;
};

const createGroupUser = async (params: {
  groupId: number;
  users: number[];
}) => {
  const { groupId, users } = params;
  const { data } = await axios.post(`/api/groups/${groupId}/users`, {
    users,
  });
  return data;
};

const deleteGroupUser = async (params: { groupId: number; userId: number }) => {
  const { groupId, userId } = params;
  const { data } = await axios.delete(`/api/groups/${groupId}/users/${userId}`);
  return data;
};

const useGroupUsers = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const {
    data: groupUsers,
    isLoading,
    refetch,
  } = useQuery(
    ["groupUsers", params?.groupId],
    () =>
      getGroupUsers({
        groupId: params?.groupId!,
      }),
    {
      enabled: immediate,
      retry: false,
    },
  );

  const createMutation = useMutation(
    ({ params }: MutationParams<{ groupId: number; users: number[] }>) =>
      createGroupUser(params),
    useCustomMutation(toast, "create"),
  );

  const deleteMutation = useMutation(
    ({ params }: MutationParams<{ groupId: number; userId: number }>) =>
      deleteGroupUser(params),
    useCustomMutation(toast, "delete"),
  );

  return {
    groupUsers,
    isLoading,
    refetch,

    createGroupUser: createMutation.mutate,
    createGroupUserAsync: createMutation.mutateAsync,
    createGroupUserLoading: createMutation.isLoading,

    deleteGroupUser: deleteMutation.mutate,
  };
};

export default useGroupUsers;
